import { datadogLogs } from '@datadog/browser-logs'
import { AppState, AppStateContext } from 'containers/State/AppState'
import { EntryType } from 'model/common'
import { API_DISTRIBUTION_FACET } from 'utils/datadog'
import {
  Matrix,
  ResponseChoice,
  ResponseOptionChoice
} from 'model/responseChoice'
import { PfmContext } from 'containers/PageFlowManager/PageFlowManager'
import { useContext } from 'react'
import { RESPONSE_CHOICE_API_PATH } from 'utils/constants'
import { getNowTimeString, getUtcOffset } from 'utils/datetime'
import { legacyApiUrlEndpoint } from 'utils/helper'
import {
  shouldUseNewApiEndpoints,
  shouldUseResponseOptionsEndpoints
} from 'utils/featureFlags'
import { useQuestionnaireParams } from './useQuestionnaireParams'
import { usePostCallbackWithRetry } from './usePostCallback'
import useSendCategoricalQuestion from './useSendCategoricalQuestion'
import useSendCategoricalMatrix from './useSendCategoricalMatrix'
import useSendSliderResponse from './useSendSliderResponse'
import useSendFreeTextResponse from './useSendFreeTextResponse'
import useSendRankedResponse from './useSendRankedResponse'
import useSendMaxDiffResponse from './useSendMaxDiffResponse'

interface Variables {
  questionId: string
  entry: EntryPositionProps
  entryType: EntryType
  questionTypeCode: string
  renderingDateTime: string
  responseChoices: ResponseOptionChoice[]
  matrix?: Matrix
  experimentalDesignVersion?: number
  allMatrixRowResponses?: Array<{
    questionId: string
    responseChoices: ResponseOptionChoice[]
    matrix: Matrix
  }>
}

interface EntryPositionProps {
  entryPosition: number
  renderedEntryPosition: number
  contextPosition: number
  renderedContextPosition: number
}

const endpoint = `${legacyApiUrlEndpoint}${RESPONSE_CHOICE_API_PATH}`

// Categorical question type codes that should be routed to the new endpoint
const CATEGORICAL_QUESTION_TYPES = [
  'basic',
  'matrix',
  'standard_audience',
  'custom_audience'
]

const useSendResponseChoice = () => {
  const mandatoryQueryParams = useQuestionnaireParams()
  const {
    respondentProgress: [progress, setProgress],
    addResponseChoicePromise
  } = useContext<AppState>(AppStateContext)
  const sendResponseChoice = usePostCallbackWithRetry<ResponseChoice>()
  const { next } = useContext(PfmContext)
  const { sendCategoricalQuestion } = useSendCategoricalQuestion()
  const { sendCategoricalMatrix } = useSendCategoricalMatrix()
  const { sendSliderResponse } = useSendSliderResponse()
  const { sendFreeTextResponse } = useSendFreeTextResponse()
  const { sendRankedResponse } = useSendRankedResponse()
  const { sendMaxDiffResponse } = useSendMaxDiffResponse()

  const sendResponseOptionChoices = ({
    questionId,
    entry,
    entryType,
    questionTypeCode,
    renderingDateTime,
    responseChoices,
    matrix,
    experimentalDesignVersion,
    allMatrixRowResponses
  }: Variables) => {
    if (!mandatoryQueryParams) {
      // NOTE: should never get here
      datadogLogs.logger.error(
        'Response choice failed to be sent. Mandatory params missing. Ensure `sid`, `rid` and `ps` are specified',
        { questionId, matrix, responseChoices }
      )
      return
    }

    // Route categorical questions to new endpoint if feature flag is enabled
    const isCategoricalQuestion =
      CATEGORICAL_QUESTION_TYPES.includes(questionTypeCode)
    const useNewEndpoint = shouldUseNewApiEndpoints(
      mandatoryQueryParams.respondentId
    )
    const useResponseOptionsEndpoint = shouldUseResponseOptionsEndpoints(
      mandatoryQueryParams.respondentId
    )

    // Route categorical matrix responses to new endpoint if feature flag is enabled
    // For matrices, check if all rows are categorical and we have the batch data
    if (
      matrix &&
      allMatrixRowResponses &&
      allMatrixRowResponses.length > 0 &&
      useNewEndpoint
    ) {
      // Check if this is a categorical matrix by looking at the first call
      // Only send once (on the first row call) - subsequent calls return early
      const isFirstRowCall = questionId === allMatrixRowResponses[0].questionId

      if (isFirstRowCall) {
        datadogLogs.logger.info('Routing categorical matrix to new endpoint', {
          matrixTitleId: matrix.matrixTitleId,
          matrixRowCount: allMatrixRowResponses.length,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW
        })

        const promise = sendCategoricalMatrix({
          entryPosition: entry.entryPosition,
          renderedEntryPosition: entry.renderedEntryPosition,
          contextPosition: entry.contextPosition,
          renderedContextPosition: entry.renderedContextPosition,
          questionTypeCode,
          allMatrixRowResponses
        })

        addResponseChoicePromise(promise)
      }

      return
    }

    if (isCategoricalQuestion && useNewEndpoint && !matrix) {
      datadogLogs.logger.info('Routing categorical question to new endpoint', {
        questionId,
        questionTypeCode,
        respondentId: mandatoryQueryParams.respondentId,
        apiUsed: API_DISTRIBUTION_FACET.NEW
      })

      const promise = sendCategoricalQuestion({
        questionId,
        entryPosition: entry.entryPosition,
        renderedEntryPosition: entry.renderedEntryPosition,
        contextPosition: entry.contextPosition,
        renderedContextPosition: entry.renderedContextPosition,
        questionTypeCode,
        enteringDatetime: renderingDateTime,
        responseChoices
      })

      addResponseChoicePromise(promise)
      return
    }

    // Route slider (scale) questions to new endpoint
    if (questionTypeCode === 'scale' && useResponseOptionsEndpoint) {
      datadogLogs.logger.info(
        'Routing slider response choice to new endpoint',
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW
        }
      )

      const promise = sendSliderResponse({
        questionId,
        entryPosition: entry.entryPosition,
        renderedEntryPosition: entry.renderedEntryPosition,
        contextPosition: entry.contextPosition,
        renderedContextPosition: entry.renderedContextPosition,
        responseChoices
      })

      addResponseChoicePromise(promise)
      return
    }

    // Route ranked questions to new endpoint
    if (questionTypeCode === 'ranked' && useResponseOptionsEndpoint) {
      datadogLogs.logger.info(
        'Routing ranked response choice to new endpoint',
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW
        }
      )

      const promise = sendRankedResponse({
        questionId,
        entryPosition: entry.entryPosition,
        renderedEntryPosition: entry.renderedEntryPosition,
        contextPosition: entry.contextPosition,
        renderedContextPosition: entry.renderedContextPosition,
        questionTypeCode,
        responseChoices
      })

      addResponseChoicePromise(promise)
      return
    }

    // Route MaxDiff questions to new endpoint
    if (questionTypeCode === 'max_diff' && useResponseOptionsEndpoint) {
      datadogLogs.logger.info(
        'Routing max_diff response choice to new endpoint',
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW
        }
      )

      const promise = sendMaxDiffResponse({
        questionId,
        entryPosition: entry.entryPosition,
        renderedEntryPosition: entry.renderedEntryPosition,
        contextPosition: entry.contextPosition,
        renderedContextPosition: entry.renderedContextPosition,
        questionTypeCode,
        responseChoices,
        experimentalDesignVersion: experimentalDesignVersion!
      })

      addResponseChoicePromise(promise)
      return
    }

    // Route free text questions to new endpoint
    if (questionTypeCode === 'free_text' && useResponseOptionsEndpoint) {
      datadogLogs.logger.info(
        'Routing free_text response choice to new endpoint',
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.NEW
        }
      )

      const promise = sendFreeTextResponse({
        questionId,
        entryPosition: entry.entryPosition,
        renderedEntryPosition: entry.renderedEntryPosition,
        contextPosition: entry.contextPosition,
        renderedContextPosition: entry.renderedContextPosition,
        responseChoices
      })

      addResponseChoicePromise(promise)
      return
    }

    // Log when categorical questions use legacy endpoint
    if (isCategoricalQuestion) {
      datadogLogs.logger.info(
        'Routing categorical question to legacy endpoint',
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          hasMatrix: !!matrix,
          apiUsed: API_DISTRIBUTION_FACET.LEGACY
        }
      )
    }

    // Log when new question types use legacy endpoint
    if (
      ['scale', 'ranked', 'max_diff', 'free_text'].includes(questionTypeCode)
    ) {
      datadogLogs.logger.info(
        `Routing ${questionTypeCode} response choice to legacy endpoint`,
        {
          questionId,
          questionTypeCode,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.LEGACY
        }
      )
    }

    const payload: ResponseChoice = {
      surveyId: mandatoryQueryParams.surveyId,
      panelSupplierCode: mandatoryQueryParams.panelSupplier,
      respondentId: mandatoryQueryParams.respondentId,
      isPreview: !!mandatoryQueryParams.preview,
      enteringDatetime: renderingDateTime,
      submissionDatetime: getNowTimeString(),
      questionId,
      matrix,
      entryPosition: entry.entryPosition,
      renderedEntryPosition: entry.renderedEntryPosition,
      previousEntryPosition: progress?.previousEntryPosition || -1,
      contextPosition: entry.contextPosition,
      renderedContextPosition: entry.renderedContextPosition,
      entryType,
      questionTypeCode,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      responseChoices,
      experimentalDesignVersion,
      utcOffset: getUtcOffset()
    }

    const requestStartTime = performance.now()

    const promise = sendResponseChoice(payload, endpoint)
      .then(() => {
        const requestDuration = performance.now() - requestStartTime
        datadogLogs.logger.info('Response choice was sent', {
          questionId,
          questionTypeCode,
          surveyId: mandatoryQueryParams.surveyId,
          respondentId: mandatoryQueryParams.respondentId,
          apiUsed: API_DISTRIBUTION_FACET.LEGACY,
          requestDurationMs: Math.round(requestDuration),
          payloadSize: JSON.stringify(payload).length
        })
      })
      .catch((err) => {
        const requestDuration = performance.now() - requestStartTime
        if (progress) {
          const newProgress = { ...progress }
          newProgress.isQualityTerminated = true
          setProgress(newProgress)
          next()
        } else {
          throw new Error(
            'Could not find respondent progress in useSendResponseChoices'
          )
        }
        datadogLogs.logger.error(
          'Network Failure: Response choice failed to be sent',
          {
            err,
            httpStatusCode: err?.response?.status ?? null,
            questionId,
            questionTypeCode,
            surveyId: mandatoryQueryParams.surveyId,
            respondentId: mandatoryQueryParams.respondentId,
            apiUsed: API_DISTRIBUTION_FACET.LEGACY,
            payloadSize: JSON.stringify(payload).length,
            requestDurationMs: Math.round(requestDuration)
          }
        )
      })

    // Add the promise to the tracker
    addResponseChoicePromise(promise)
  }

  return sendResponseOptionChoices
}

export default useSendResponseChoice
